<template>
  <div class="d-flex flex-column ga-4">
    <v-alert type="info" density="comfortable">
      {{ $t("ncrm.workflows.nodeSettings.mailjetLeadAdd.hint") }}
    </v-alert>

    <v-switch
      v-model="form.status"
      :true-value="'active'"
      :false-value="'inactive'"
      color="primary"
      hide-details
      :label="$t('ncrm.workflows.nodeSettings.active')"
    />

    <v-select
      v-model="form.boardId"
      :items="boardOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.workflows.nodeSettings.mailjetLeadAdd.board')"
      :loading="loadingBoards"
      hide-details="auto"
      :menu-props="selectMenuProps"
      @update:model-value="onBoardChange"
    />

    <v-select
      v-model="form.stageId"
      :items="stageOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.workflows.nodeSettings.mailjetLeadAdd.stage')"
      :loading="loadingStages"
      :disabled="!form.boardId"
      hide-details="auto"
      :menu-props="selectMenuProps"
    />

    <v-select
      v-model="form.priority"
      :items="priorityOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.workflows.nodeSettings.mailjetLeadAdd.priority')"
      hide-details="auto"
      :menu-props="selectMenuProps"
    />

    <v-alert v-if="loadError" type="warning" density="compact">
      {{ loadError }}
    </v-alert>

    <v-divider />

    <v-switch
      v-model="form.autoRun"
      color="primary"
      hide-details
      :label="form.autoRun
        ? $t('ncrm.workflows.nodeSettings.autoSyncOn')
        : $t('ncrm.workflows.nodeSettings.autoSyncOff')"
    />
    <v-select
      v-if="form.autoRun"
      v-model="form.autoRunIntervalMinutes"
      :items="intervalOptions"
      item-title="label"
      item-value="value"
      :label="$t('ncrm.workflows.nodeSettings.mailjetLeadAdd.autoRunInterval')"
      hide-details="auto"
      :menu-props="selectMenuProps"
    />
  </div>
</template>

<script>
import axios from "axios";
import { MAILJET_LEAD_PRIORITIES } from "@/Utile/ncrmMailjetEngagementWorkflow.mjs";

export default {
  name: "NcrmWorkflowMailjetLeadAddSettings",
  props: {
    settings: { type: Object, default: () => ({}) },
    intervalOptions: { type: Array, default: () => [] },
    selectMenuProps: { type: Object, default: () => ({ maxHeight: 320, zIndex: 2710 }) },
  },
  emits: ["update:value"],
  data() {
    return { form: this.buildForm(this.settings), boards: [], stages: [], loadingBoards: false, loadingStages: false, loadError: "" };
  },
  computed: {
    boardOptions() {
      return this.boards.map((b) => ({ value: b.id, label: b.name }));
    },
    stageOptions() {
      return this.stages.map((s) => ({ value: s.id, label: s.name }));
    },
    priorityOptions() {
      return MAILJET_LEAD_PRIORITIES.map((value) => ({
        value,
        label: this.$t(`ncrm.workflows.nodeSettings.mailjetLeadAdd.priorities.${value}`),
      }));
    },
  },
  watch: {
    settings: { deep: true, handler(next) { this.form = this.buildForm(next); this.emitValue(); if (this.form.boardId) this.fetchStages(this.form.boardId); } },
    form: { deep: true, handler() { this.emitValue(); } },
  },
  mounted() {
    this.fetchBoards();
    this.emitValue();
  },
  methods: {
    buildForm(settings = {}) {
      const priority = String(settings.priority || "");
      return {
        status: settings.status === "active" ? "active" : settings.status === "inactive" ? "inactive" : "active",
        autoRun: Boolean(settings.autoRun),
        autoRunIntervalMinutes: Number(settings.autoRunIntervalMinutes) > 0 ? Math.trunc(Number(settings.autoRunIntervalMinutes)) : 15,
        boardId: Number(settings.boardId) > 0 ? Number(settings.boardId) : null,
        stageId: Number(settings.stageId) > 0 ? Number(settings.stageId) : null,
        priority: MAILJET_LEAD_PRIORITIES.includes(priority) ? priority : "high",
      };
    },
    emitValue() {
      this.$emit("update:value", {
        status: this.form.status,
        autoRun: this.form.autoRun,
        autoRunIntervalMinutes: this.form.autoRunIntervalMinutes,
        boardId: this.form.boardId || null,
        stageId: this.form.stageId || null,
        priority: this.form.priority,
      });
    },
    onBoardChange(boardId) {
      this.form.stageId = null;
      this.fetchStages(boardId);
    },
    async fetchBoards() {
      this.loadingBoards = true;
      this.loadError = "";
      try {
        const { data } = await axios.get("/ncrm/leads/boards");
        this.boards = (Array.isArray(data?.items) ? data.items : []).filter((b) => b.is_active !== false && b.is_active !== 0);
        if (this.form.boardId) await this.fetchStages(this.form.boardId);
      } catch (err) {
        this.boards = [];
        this.loadError = err?.response?.data?.error || this.$t("ncrm.workflows.nodeSettings.leadBoard.loadBoardsError");
      } finally {
        this.loadingBoards = false;
      }
    },
    async fetchStages(boardId) {
      const id = Number(boardId);
      if (!Number.isFinite(id) || id <= 0) { this.stages = []; return; }
      this.loadingStages = true;
      try {
        const { data } = await axios.get(`/ncrm/leads/boards/${id}/stages`);
        this.stages = (Array.isArray(data?.items) ? data.items : []).filter((s) => s.is_active !== false && s.is_active !== 0);
        if (this.form.stageId && !this.stages.some((s) => Number(s.id) === Number(this.form.stageId))) this.form.stageId = null;
      } catch (err) {
        this.stages = [];
        this.loadError = err?.response?.data?.error || this.$t("ncrm.workflows.nodeSettings.leadBoard.loadStagesError");
      } finally {
        this.loadingStages = false;
      }
    },
  },
};
</script>
